import React, { useMemo, useRef, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table';
import { MasterFlightRecord, formatFrequencyWithUnderscores, getDisplayFrequency, getDisplayDate, getRouteString, formatDisplayDate } from '../../../utils/types';
import { useTimezone } from '../../../contexts/TimezoneContext';
import { Cell } from '../common';
import { ExpandedRowDetail } from './ExpandedRowDetail';
import { EmptyState } from './EmptyState';
import { Pagination, TableSearch, AdvancedFilters } from '../../common';
import EditIcon from '@mui/icons-material/Edit';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface FlightTableProps {
  records: MasterFlightRecord[];
  onEdit?: (record: MasterFlightRecord) => void;
  onDelete?: (id: string) => void;
  onCreateVariant?: (record: MasterFlightRecord) => void;
  onAdd?: () => void;
  onAddAdhoc?: () => void;
  // Search props
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  totalRecords?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

export function FlightTable({
  records,
  onEdit,
  onDelete,
  onCreateVariant,
  onAdd,
  onAddAdhoc,
  searchValue = '',
  onSearchChange,
  currentPage = 1,
  totalPages = 1,
  totalRecords = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange
}: FlightTableProps) {
  const { timezone } = useTimezone();
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'startDate', desc: false }  // Earliest first (ascending)
  ]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    owner: '',
    dateSort: 'start-asc',  // Match default sorting
    seasonId: '',
    serviceType: '',
    destination: '',
    sectors: '',
  });

  // Handle advanced filter changes and update table sorting accordingly
  const handleAdvancedFiltersChange = (filters: AdvancedFilters) => {
    setAdvancedFilters(filters);

    // Update table sorting based on date sort filter
    if (filters.dateSort === 'none' || filters.dateSort === 'start-asc') {
      // Default sorting: Start Date (Earliest First)
      setSorting([{ id: 'startDate', desc: false }]);
    } else if (filters.dateSort === 'start-desc') {
      setSorting([{ id: 'startDate', desc: true }]);
    } else if (filters.dateSort === 'end-asc') {
      setSorting([{ id: 'endDate', desc: false }]);
    } else if (filters.dateSort === 'end-desc') {
      setSorting([{ id: 'endDate', desc: true }]);
    }
  };

  // State for managing which variant to display in expanded row
  const [displayedVariant, setDisplayedVariant] = useState<MasterFlightRecord | null>(null);

  // Sync displayedVariant with updated records from API
  // When a flight is updated via Redux, the records prop will refresh with new data
  // We need to update the displayedVariant state to reflect the changes (e.g., station updates)
  React.useEffect(() => {
    if (displayedVariant?._id) {
      // Find the updated version of the displayed variant in the refreshed records
      const updatedVariant = records.find(r => r._id === displayedVariant._id);
      if (updatedVariant) {
        // Update displayedVariant with fresh data from API
        setDisplayedVariant(updatedVariant);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  // Extract all filter dropdown options in a single pass over records
  const filterOptions = useMemo(() => {
    const owners = new Set<string>();
    const sectorCounts = new Set<number>();
    const seasons = new Set<string>();
    const serviceTypes = new Set<string>();
    const destinations = new Set<string>();

    records.forEach(record => {
      if (record.flightOwner) owners.add(record.flightOwner);
      if (record.seasonId) seasons.add(record.seasonId);
      if (record.iataServiceTypeCode) serviceTypes.add(record.iataServiceTypeCode);
      const count = record.stations?.length || 0;
      if (count > 0) sectorCounts.add(count);
      record.stations?.forEach(station => {
        if (station.departureStation) destinations.add(station.departureStation);
        if (station.arrivalStation) destinations.add(station.arrivalStation);
      });
    });

    return {
      owners: Array.from(owners).sort(),
      sectorCounts: Array.from(sectorCounts).sort((a, b) => a - b),
      seasons: Array.from(seasons).sort(),
      serviceTypes: Array.from(serviceTypes).sort(),
      destinations: Array.from(destinations).sort(),
    };
  }, [records]);

  // Apply advanced filters and deduplicate records
  const deduplicatedRecords = useMemo(() => {
    const seen = new Map<string, MasterFlightRecord>();

    // Filter records
    let filteredRecords = records;

    // Filter by owner
    if (advancedFilters.owner) {
      filteredRecords = filteredRecords.filter(r => r.flightOwner === advancedFilters.owner);
    }

    // Filter by season ID
    if (advancedFilters.seasonId) {
      filteredRecords = filteredRecords.filter(r => r.seasonId === advancedFilters.seasonId);
    }

    // Filter by service type
    if (advancedFilters.serviceType) {
      filteredRecords = filteredRecords.filter(r => r.iataServiceTypeCode === advancedFilters.serviceType);
    }

    // Filter by destination (any station contains the destination)
    if (advancedFilters.destination) {
      filteredRecords = filteredRecords.filter(r =>
        r.stations?.some(s =>
          s.departureStation === advancedFilters.destination ||
          s.arrivalStation === advancedFilters.destination
        )
      );
    }

    // Filter by sectors
    if (advancedFilters.sectors) {
      const sectorCount = parseInt(advancedFilters.sectors);
      filteredRecords = filteredRecords.filter(r =>
        r.stations?.length === sectorCount
      );
    }

    // Note: Date sorting is now handled by table sorting state (setSorting in handleAdvancedFiltersChange)

    // Deduplicate - show only one row per flight number
    filteredRecords.forEach(record => {
      const flightNoDigits = record.flightNo.replace(/[^0-9]/g, '');
      const key = `${record.flightOwner}-${flightNoDigits}`;

      if (!seen.has(key)) {
        seen.set(key, record);
      }
    });

    return Array.from(seen.values());
  }, [records, advancedFilters]);

  // Define columns
  const columns = useMemo<ColumnDef<MasterFlightRecord>[]>(
    () => [
      {
        id: 'expand',
        header: '',
        enableSorting: false,
        size: 40,
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <ChevronRightIcon
              sx={{ fontSize: 16 }}
              className={`transition-transform duration-200
                ${row.id === expandedKey ? 'rotate-90 text-primary-500' : 'rotate-0 text-gray-400'}`}
            />
          </div>
        ),
      },
      {
        accessorKey: 'flightNo',
        header: 'Flight No',
        cell: ({ row }) => (
          <span className="text-center flex items-center justify-center gap-1.5">
            {row.original.flightNo}
            {row.original.isAdhoc && (
              <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold uppercase leading-none">
                Adhoc
              </span>
            )}
          </span>
        ),
      },
      {
        accessorKey: 'flightOwner',
        header: 'Owner',
        cell: ({ getValue }) => (
          <span className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-md text-base font-semibold">
            {getValue() as string}
          </span>
        ),
      },
      {
        accessorKey: 'seasonId',
        header: 'Season',
      },
      {
        accessorKey: 'startDate',
        header: 'Start Date',
        cell: ({ row }) => (
          <span className="text-gray-600 text-md">{formatDisplayDate(getDisplayDate(row.original.startDate, row.original.stations?.[0]?.std, timezone))}</span>
        ),
      },
      {
        accessorKey: 'endDate',
        header: 'End Date',
        cell: ({ row }) => (
          <span className="text-gray-600 text-md">{formatDisplayDate(getDisplayDate(row.original.endDate, row.original.stations?.[0]?.std, timezone))}</span>
        ),
      },
      {
        accessorKey: 'frequency',
        header: 'Frequency',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-gray-600 text-md">{formatFrequencyWithUnderscores(getDisplayFrequency(row.original.frequency, row.original.frequencyMYT, timezone))}</span>
        ),
      },
      {
        accessorKey: 'iataServiceTypeCode',
        header: 'Service',
        enableSorting: false,
        cell: ({ getValue }) => (
          <span className="text-gray-500">{getValue() as string}</span>
        ),
      },
      {
        id: 'route',
        header: 'Route',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-gray-600 text-md">
            {getRouteString(row.original.stations)}
          </span>
        ),
      },
      {
        id: 'sectors',
        header: 'Sectors',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="inline-flex items-center justify-center w-7 h-7 bg-primary-100 text-primary-500 rounded-md text-base font-bold">
            {row.original.stations?.length || 0}
          </span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex gap-2 justify-center">
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row.original);
                }}
                className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-primary-500 text-base font-medium cursor-pointer hover:opacity-95"
              >
                <EditIcon sx={{ fontSize: 16 }} />
                Edit
              </button>
            )}
          </div>
        ),
      },
    ],
    [onEdit, expandedKey, timezone]
  );

  // Initialize table — filtering is handled server-side via search query param
  const table = useReactTable({
    data: deduplicatedRecords,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Use row._id as unique identifier
    getRowId: (row) => row._id || row.csfsKey || String(Math.random()),
  });

  const collapseTimerRef = useRef<NodeJS.Timeout | null>(null);

  const toggleExpand = (id: string, record: MasterFlightRecord) => {
    // Clear any pending collapse cleanup
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }

    if (expandedKey === id) {
      // Collapsing — clear expandedKey immediately (triggers 1fr→0fr),
      // delay clearing displayedVariant so component stays mounted during animation
      setExpandedKey(null);
      collapseTimerRef.current = setTimeout(() => {
        setDisplayedVariant(null);
      }, 450);
    } else {
      // Expanding — mount component first at 0fr, then animate to 1fr
      setExpandedKey(null); // ensure isExpanded starts false
      setDisplayedVariant(record);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setExpandedKey(id);
        });
      });
    }
  };

  // Get all variants for the currently displayed variant
  const variants = useMemo(() => {
    if (!displayedVariant) return [];

    // Extract flight number digits (remove owner prefix)
    const flightNoDigits = displayedVariant.flightNo.replace(/[^0-9]/g, '');

    // Find all records with same owner and flight number
    return records.filter(record => {
      const recordFlightNoDigits = record.flightNo.replace(/[^0-9]/g, '');
      return (
        record.flightOwner === displayedVariant.flightOwner &&
        recordFlightNoDigits === flightNoDigits
      );
    });
  }, [displayedVariant, records]);

  // Handle variant selection - switch displayed variant
  const handleVariantSelect = (variant: MasterFlightRecord) => {
    setDisplayedVariant(variant);
  };

  const rowCount = table.getRowModel().rows.length;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm" style={{ overflowAnchor: 'none' }}>
      {/* Search - Always visible */}
      <TableSearch
        value={searchValue}
        onChange={(val) => onSearchChange?.(val)}
        placeholder="Search by flight number, route, airline..."
        filteredCount={totalRecords}
        totalCount={totalRecords}
        onFiltersChange={handleAdvancedFiltersChange}
        ownerOptions={filterOptions.owners}
        seasonIdOptions={filterOptions.seasons}
        serviceTypeOptions={filterOptions.serviceTypes}
        sectorOptions={filterOptions.sectorCounts}
        destinationOptions={filterOptions.destinations}
        onAdd={onAdd}
        onAddAdhoc={onAddAdhoc}
      />

      {/* Table or Empty State */}
      {!deduplicatedRecords.length ? (
        <EmptyState />
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto scrollbar-thin" style={{ overflowY: 'hidden', overflowAnchor: 'none' }}>
            <table className="w-full border-collapse">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const canSort = header.column.getCanSort();
                  const isSorted = header.column.getIsSorted();
                  const isExpandColumn = header.column.id === 'expand';

                  return (
                    <th
                      key={header.id}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      className={`
                        text-center text-base font-semibold text-gray-700 bg-gray-50 border-b border-gray-200
                        ${canSort ? 'cursor-pointer select-none hover:bg-gray-100' : ''}
                        ${isExpandColumn ? 'w-10 px-0' : 'px-4 py-2'}
                      `}
                    >
                      <div className="flex items-center justify-center gap-2">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className="text-gray-400">
                            {isSorted === 'asc' ? '↑' : isSorted === 'desc' ? '↓' : '↕'}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row, idx) => {
              const isExpanded = expandedKey === row.id;

              return (
                <React.Fragment key={row.id}>
                  <tr
                    onClick={() => toggleExpand(row.id, row.original)}
                    className={`cursor-pointer transition-colors
                      ${isExpanded
                        ? 'bg-blue-50'
                        : `${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-50`
                      }`}
                  >
                    {row.getVisibleCells().map((cell) => {
                      // Special styling for columns
                      const isExpandColumn = cell.column.id === 'expand';
                      const isFlightNoColumn = cell.column.id === 'flightNo';

                      return (
                        <Cell
                          key={cell.id}
                          center
                          noBorder={isExpanded}
                          className={`${isFlightNoColumn ? 'font-semibold text-primary-500 text-lg' : ''} ${isExpandColumn ? 'w-10 px-0' : ''}`}
                          onClick={cell.column.id === 'actions' ? (e) => e.stopPropagation() : undefined}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Cell>
                      );
                    })}
                  </tr>

                  {displayedVariant && (
                    <ExpandedRowDetail
                      isExpanded={isExpanded}
                      record={displayedVariant}
                      variants={variants}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onCreateVariant={onCreateVariant}
                      onVariantSelect={handleVariantSelect}
                      onCollapse={() => {
                        if (collapseTimerRef.current) {
                          clearTimeout(collapseTimerRef.current);
                        }
                        setExpandedKey(null);
                        collapseTimerRef.current = setTimeout(() => {
                          setDisplayedVariant(null);
                        }, 450);
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer with Pagination */}
      {onPageChange ? (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={deduplicatedRecords.length}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          recordCountLabel="Click any row to expand details"
        />
      ) : (
        <div className="px-5 py-3 border-t border-gray-200 flex justify-between items-center text-base text-gray-500 bg-gray-50">
          <span>Click any row to expand details</span>
          <span>{rowCount} records</span>
        </div>
      )}
        </>
      )}
    </div>
  );
}
